<script setup>
import { BButton } from "buefy";
import { ref, reactive, watch } from "vue";
import FilterTextInput  from "./FilterTextInput.vue";
import FilterDateRange  from "./FilterDateRange.vue";
import FilterSelect     from "./FilterSelect.vue";
import FilterNumberRange from "./FilterNumberRange.vue";
import FilterTrainingTags from "@/components/filters/FilterTrainingTags.vue";
import FilterCheckbox from "@/components/filters/FilterCheckbox.vue";

const props = defineProps({
    filters: { type: Array, required: true },
});

const emit = defineEmits(["update:activeFilters"]);

const showFilters = ref(false);

const buildInitialState = () => {
    const state = {};
    for (const filter of props.filters) {
        if      (filter.type === "text")   state[filter.key] = null;
        else if (filter.type === "date")   state[filter.key] = { from: null, to: null };
        else if (filter.type === "select") state[filter.key] = null;
        else if (filter.type === "trainingTags") state[filter.key] = null;
        else if (filter.type === "number") state[filter.key] = { from: null, to: null };
        else if (filter.type === "checkbox") state[filter.key] = null;
    }
    return state;
};

const activeFilters = reactive(buildInitialState());

watch(activeFilters, (val) => {
    for (const key of Object.keys(val)) {
        if (val[key] === '') val[key] = null;
    }
    emit("update:activeFilters", JSON.parse(JSON.stringify(val)));
    if (!showFilters.value) {
        showFilters.value = true;
    }
}, { deep: true });

const filterRefs = ref({});

const toggleFilters = () => {
    showFilters.value = !showFilters.value;
};

const clearFilters = () => {
    const fresh = buildInitialState();
    for (const key of Object.keys(fresh)) {
        const val = fresh[key];
        activeFilters[key] = (typeof val === "object" && val !== null) ? { ...val } : val;
    }
    for (const ref of Object.values(filterRefs.value)) {
        ref?.clear?.();
    }
};

const setFilter = (key, value) => {
    activeFilters[key] = value;

    let filter = props.filters.find((filter) => filter.key === key);

    if (filter && filter.type === 'trainingTags') {
        filterRefs.value[key].setTag(value);
    }
}

defineExpose({
    setFilter
})
</script>

<template>
    <div class="filters">
        <div class="buttons">
            <slot name="buttons"/>

            <b-button
                type="is-primary"
                :title="$t('filters.filters_label')"
                icon-left="filter-variant"
                @click="toggleFilters"
            >
                {{ $t('filters.filters_label') }}
            </b-button>
        </div>

        <div v-show="showFilters" class="box my-4">
            <div class="filters-grid">
                <template v-for="filter in filters" :key="filter.key">

                    <FilterTextInput
                        v-if="filter.type === 'text'"
                        :ref="(el) => filterRefs[filter.key] = el"
                        :filter-id="filter.key"
                        :label="filter.label"
                        :placeholder="filter.placeholder || filter.label"
                        :tooltip="filter.tooltip"
                        v-model="activeFilters[filter.key]"
                    />

                    <FilterDateRange
                        v-else-if="filter.type === 'date'"
                        :ref="(el) => filterRefs[filter.key] = el"
                        :filter-id="filter.key"
                        :label="filter.label"
                        :model-value="activeFilters[filter.key]"
                        @update:modelValue="activeFilters[filter.key] = $event"
                    />

                    <FilterSelect
                        v-else-if="filter.type === 'select'"
                        :ref="(el) => filterRefs[filter.key] = el"
                        :filter-id="filter.key"
                        :label="filter.label"
                        :options="filter.options"
                        :model-value="activeFilters[filter.key]"
                        @update:modelValue="activeFilters[filter.key] = $event"
                    />

                    <FilterNumberRange
                        v-else-if="filter.type === 'number'"
                        :ref="(el) => filterRefs[filter.key] = el"
                        :filter-id="filter.key"
                        :label="filter.label"
                        :model-value="activeFilters[filter.key]"
                        @update:modelValue="activeFilters[filter.key] = $event"
                    />

                    <FilterTrainingTags
                        v-else-if="filter.type === 'trainingTags'"
                        :ref="(el) => filterRefs[filter.key] = el"
                        :filter-id="filter.key"
                        :label="filter.label"
                        v-model="activeFilters[filter.key]"
                    />

                    <FilterCheckbox
                        v-else-if="filter.type === 'checkbox'"
                        :ref="(el) => filterRefs[filter.key] = el"
                        :filter-id="filter.key"
                        :label="filter.label"
                        v-model="activeFilters[filter.key]"
                    />

                </template>
            </div>

            <div class="filters-actions">
                <b-button type="is-secondary" size="is-small" @click="clearFilters">
                    {{ $t('filters.clear_filters') }}
                </b-button>
            </div>
        </div>
    </div>
</template>

<style scoped>

.filters-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
}

.filters-actions {
    margin-top: 1rem;
    display: flex;
    justify-content: flex-end;
}
</style>
